import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { Action } from "../../generated/WorkflowsApi.types";
import type { ActionWithRules } from "../../types";

interface ActionContextValue {
  action: Action;
  index: number;
  isSaved: boolean;
  isViewOnlyMode?: boolean;
  paramsPath: string;
  path: string;
}

const CurrentActionContext = createContext<ActionContextValue | null>(null);
CurrentActionContext.displayName = "CurrentActionContext";

export const useCurrentAction = (): ActionContextValue => {
  const currentAction = useContext(CurrentActionContext);
  if (!currentAction) {
    throw new Error("useCurrentAction must be used within a Action");
  }
  return currentAction;
};

interface ActionProviderProps {
  action: ActionWithRules;
  children: ReactNode;
  index?: number;
  isViewOnlyMode?: boolean;
}

export const ActionProvider = ({
  action,
  index = 0,
  children,
  isViewOnlyMode = false,
}: ActionProviderProps) => {
  const path = `actions.${index}`;
  const paramsPath = `${path}.api_params`;
  const isSaved =
    typeof action?.id === "string" && !action.id.startsWith("action");

  return (
    <CurrentActionContext.Provider
      value={{ action, index, isSaved, paramsPath, path, isViewOnlyMode }}
    >
      {children}
    </CurrentActionContext.Provider>
  );
};
